import { useState } from "react";
import { toast } from "sonner";
import { FileDown, DatabaseBackup, Github, Server, ShieldCheck, Bot, Loader2 } from "lucide-react";
import { api, errMsg } from "../lib/api";
import { Card, PageTitle, Btn, Toggle } from "./ui";

const download = async (url, fallback, setBusy, key) => {
  setBusy(key);
  try {
    const res = await api.get(url, { responseType: "blob", timeout: 600000 });
    const name = (res.headers["content-disposition"] || "").match(/filename="([^"]+)"/)?.[1] || fallback;
    const href = URL.createObjectURL(res.data);
    const a = Object.assign(document.createElement("a"), { href, download: name });
    document.body.appendChild(a); a.click(); a.remove();
    setTimeout(() => URL.revokeObjectURL(href), 2000);
    toast.success(`${name} downloaded`);
  } catch (e) { toast.error(errMsg(e)); } finally { setBusy(""); }
};

const STEPS = [
  [Github, "Export the code", "Click Save to GitHub in Emergent to get the complete source code in your own repository."],
  [DatabaseBackup, "Download your data", "Download the site backup below: settings, content, posts, exchanges, proofs, admin users and all images."],
  [Server, "Install on the VPS", "Follow the PDF: Ubuntu, Python, Node, MongoDB, Nginx, systemd service and free SSL for daddyanna247.com."],
  [Bot, "Add an AI key", "Use your Universal Key or your own Google (Gemini) / Claude key for the AI writer and images."],
  [ShieldCheck, "Daily backups", "The guide sets up nightly database + uploads backups that keep the last 7 days."],
];

export default function InstallGuide() {
  const [busy, setBusy] = useState("");
  const [history, setHistory] = useState(false);
  return (
    <div data-testid="admin-install-guide">
      <PageTitle title="Install Guide" sub="Move DaddyAnna247 to your own Bluehost VPS with a step-by-step PDF" />
      <div className="grid grid-cols-1 gap-6 xl:grid-cols-[minmax(0,1.2fr)_minmax(0,1fr)] [&>*]:min-w-0">
        <Card title="Bluehost VPS install guide (PDF)" testid="install-guide-card">
          <p className="text-sm text-zinc-400">A printable guide with every command to copy: server setup, MongoDB, backend settings (.env keys explained), React build, Nginx, HTTPS, moving your data, AI keys, background jobs, backups, updates and troubleshooting.</p>
          <Btn onClick={() => download("/admin/maintenance/install-guide.pdf", "DaddyAnna247-Install-Guide.pdf", setBusy, "pdf")} disabled={!!busy} className="mt-5" data-testid="install-guide-download">
            {busy === "pdf" ? <Loader2 size={15} className="animate-spin" /> : <FileDown size={15} />} Download PDF
          </Btn>
          <ol className="mt-8 space-y-4">
            {STEPS.map(([Icon, t, d], i) => (
              <li key={t} className="flex gap-4" data-testid={`install-step-${i + 1}`}>
                <span className="grid h-9 w-9 shrink-0 place-items-center rounded-full border border-[#D4AF37]/40 text-[#D4AF37]"><Icon size={16} /></span>
                <div><p className="text-sm font-semibold text-white">{i + 1}. {t}</p><p className="text-sm text-zinc-500">{d}</p></div>
              </li>
            ))}
          </ol>
        </Card>
        <Card title="Site backup (.zip)" testid="backup-card">
          <p className="text-sm text-zinc-400">Everything needed to restore the site on the VPS with <span className="font-mono text-[#F3D77A]">restore_backup.py</span>. Keep this file private: it contains your admin login and settings.</p>
          <div className="mt-4"><Toggle checked={history} onChange={setHistory} testid="backup-history" label="Include visitor analytics history (bigger file)" /></div>
          <Btn variant="outline" onClick={() => download(`/admin/maintenance/backup.zip${history ? "?history=true" : ""}`, "daddyanna247-backup.zip", setBusy, "zip")} disabled={!!busy} className="mt-5" data-testid="backup-download">
            {busy === "zip" ? <Loader2 size={15} className="animate-spin" /> : <DatabaseBackup size={15} />} {busy === "zip" ? "Preparing backup…" : "Download site backup"}
          </Btn>
        </Card>
      </div>
    </div>
  );
}
